import { useCallback, useId, useRef, useState, type ReactNode, type Ref, type RefObject } from 'react';
import { Warning, X } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';
import { IconButton } from '../IconButton/IconButton';
import { Portal } from '../Portal';
import { useDismiss } from '../hooks/useDismiss';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useScrollLock } from '../hooks/useScrollLock';

export type DialogPanelProps = {
  title: ReactNode;
  children?: ReactNode;
  /** Buttons, right-aligned; put the confirming action last. */
  actions?: ReactNode;
  /** Accessible name of the close button, localised by the caller. Omit to hide the button. */
  closeLabel?: string;
  onClose?: () => void;
  titleId?: string;
  panelRef?: Ref<HTMLDivElement>;
  /**
   * A destructive confirm (delete, remove): shows a warning icon beside the title. Pair it with a
   * `variant="destructive"` Button as the confirming action. No warning hue exists, so icon + words carry it.
   */
  destructive?: boolean;
  className?: string;
};

/** The dialog surface on its own (used by Dialog, and by the showcase to draw it open). */
export function DialogPanel({
  title,
  children,
  actions,
  closeLabel,
  onClose,
  titleId,
  panelRef,
  destructive,
  className,
}: DialogPanelProps) {
  const ownId = `dp${useId().replace(/:/g, '')}`;
  const headingId = titleId ?? ownId;
  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      className={cx(
        'flex w-full max-w-110 flex-col gap-4 rounded-jc-container bg-jc-surface p-6 text-jc-ink shadow-jc-lg motion-safe:animate-jc-pop',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        {destructive ? <Icon icon={Warning} size="lg" weight="fill" className="mt-0.5 shrink-0" /> : null}
        <h2 id={headingId} className="min-w-0 flex-1 type-title text-jc-ink">
          {title}
        </h2>
        {closeLabel ? (
          <IconButton icon={X} label={closeLabel} variant="ghost" size="sm" onClick={onClose} className="-mr-2 -mt-1" />
        ) : null}
      </div>
      {children ? <div className="text-sm text-jc-ink-secondary">{children}</div> : null}
      {actions ? <div className="flex flex-wrap justify-end gap-2">{actions}</div> : null}
    </div>
  );
}

export type DialogProps = Omit<DialogPanelProps, 'titleId' | 'panelRef' | 'onClose'> & {
  open: boolean;
  onClose: () => void;
  /** Element to focus on open; defaults to the first focusable (put the safe choice first for destructive confirms). */
  initialFocus?: RefObject<HTMLElement | null>;
};

/**
 * A short, blocking question or confirmation. Modal: traps focus, locks page scroll, closes on
 * Escape and on a press on the scrim, and returns focus to whatever opened it.
 */
export function Dialog({ open, onClose, initialFocus, ...panel }: DialogProps) {
  const titleId = `dl${useId().replace(/:/g, '')}`;
  const panelRef = useRef<HTMLDivElement | null>(null);
  // The portal can mount a commit after `open` flips (first overlay before hydration), so the trap
  // waits for the panel element itself.
  const [mounted, setMounted] = useState(false);
  const attach = useCallback((el: HTMLDivElement | null) => {
    panelRef.current = el;
    setMounted(!!el);
  }, []);
  useFocusTrap(panelRef, open && mounted, { initialFocus });
  useScrollLock(open);
  useDismiss({ open, onDismiss: onClose, refs: [panelRef], outsidePress: false });
  if (!open) return null;
  return (
    <Portal>
      <div
        className="fixed inset-0 z-(--jc-z-dialog) flex items-center justify-center bg-jc-scrim p-4 motion-safe:animate-jc-fade"
        onPointerDown={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <DialogPanel {...panel} onClose={onClose} titleId={titleId} panelRef={attach} />
      </div>
    </Portal>
  );
}
